import DownloadIcon from "@mui/icons-material/Download";
import Forward10Icon from "@mui/icons-material/Forward10";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PauseIcon from "@mui/icons-material/Pause";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Replay10Icon from "@mui/icons-material/Replay10";
import SettingsIcon from "@mui/icons-material/Settings";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import IconButton from "@mui/material/IconButton";
import { useEffect, useRef, useState } from "react";
import useClickOutside from "../../hooks/useClickOutside";
import s from "./VideoPlayer.module.css";

type VideoPlayerProps = {
  src: string;
  poster?: string;
  title?: string;
  allowDownload?: boolean;
  fileName?: string;
  className?: string;
  defaultFullscreen?: boolean;
};

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

const formatTime = (value: number) => {
  if (!Number.isFinite(value)) return "00:00";
  const totalSeconds = Math.floor(value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

const sanitizeFileName = (value: string) =>
  value.replace(/[\\/:*?"<>|]+/g, " ").trim() || "video";

export default function VideoPlayer({
  src,
  poster,
  title,
  allowDownload = false,
  fileName,
  className,
  defaultFullscreen = false,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hideTimer = useRef<number | undefined>(undefined);
  const autoFullscreenRequestedRef = useRef(false);
  const speedControlRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(speedControlRef, () => setShowSpeedMenu(false));
  useClickOutside(mobileMenuRef, () => setIsMobileMenuOpen(false));

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const progressPercent = duration
    ? Math.min((currentTime / duration) * 100, 100)
    : 0;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateTime = () => setCurrentTime(video.currentTime);
    const updateDuration = () => setDuration(video.duration || 0);
    const updateVolume = () => {
      setVolume(video.volume);
      setIsMuted(video.muted || video.volume === 0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => {
      setIsPlaying(false);
      setControlsVisible(true);
    };
    const handleWaiting = () => setIsBuffering(true);
    const handlePlaying = () => setIsBuffering(false);

    video.addEventListener("timeupdate", updateTime);
    video.addEventListener("loadedmetadata", updateDuration);
    video.addEventListener("durationchange", updateDuration);
    video.addEventListener("volumechange", updateVolume);
    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("playing", handlePlaying);

    return () => {
      video.removeEventListener("timeupdate", updateTime);
      video.removeEventListener("loadedmetadata", updateDuration);
      video.removeEventListener("durationchange", updateDuration);
      video.removeEventListener("volumechange", updateVolume);
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("playing", handlePlaying);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isActive = Boolean(document.fullscreenElement);
      setIsFullscreen(isActive);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    return () => {
      if (hideTimer.current) {
        window.clearTimeout(hideTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!defaultFullscreen || autoFullscreenRequestedRef.current) return;

    autoFullscreenRequestedRef.current = true;

    const container = containerRef.current;
    const video = videoRef.current;

    if (document.fullscreenElement) {
      setIsFullscreen(true);
      return;
    }

    const requestFullscreen = async () => {
      try {
        if (container?.requestFullscreen) {
          await container.requestFullscreen();
          return;
        }

        const videoWithWebkitApi = video as
          | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
          | null;
        videoWithWebkitApi?.webkitEnterFullscreen?.();
      } catch (error) {
        console.error("Fullscreen error:", error);
      }
    };

    void requestFullscreen();
  }, [defaultFullscreen]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  const handleSeek = (value: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = value;
    setCurrentTime(value);
  };

  const handleVolumeChange = (value: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.volume = value;
    video.muted = value === 0;
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
  };

  const handleSpeedChange = (speed: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = speed;
    setPlaybackRate(speed);
    setShowSpeedMenu(false);
  };

  const handleSkip = (delta: number) => {
    const video = videoRef.current;
    if (!video) return;
    const next = Math.min(Math.max(video.currentTime + delta, 0), duration);
    video.currentTime = next;
    setCurrentTime(next);
  };

  const togglePictureInPicture = async () => {
    const video = videoRef.current;
    if (!video || !document.pictureInPictureEnabled) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
      setIsMobileMenuOpen(false);
    } catch (error) {
      console.error("Picture-in-picture error:", error);
    }
  };

  const requestPlayerFullscreen = async () => {
    const container = containerRef.current;
    const video = videoRef.current;

    if (container?.requestFullscreen) {
      await container.requestFullscreen();
      return;
    }

    const videoWithWebkitApi = video as
      | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
      | null;
    videoWithWebkitApi?.webkitEnterFullscreen?.();
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else {
      void requestPlayerFullscreen().catch((error) => {
        console.error("Fullscreen error:", error);
      });
    }
    setIsMobileMenuOpen(false);
  };

  const handleMouseMove = () => {
    setControlsVisible(true);
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
    }
    if (isPlaying) {
      hideTimer.current = window.setTimeout(() => {
        setControlsVisible(false);
      }, 2400);
    }
  };

  const handleDownload = async () => {
    if (!allowDownload) return;
    try {
      setIsDownloading(true);
      const response = await fetch(src);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `${sanitizeFileName(fileName || title || "video")}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Video download error:", error);
    } finally {
      setIsDownloading(false);
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${s.player} ${className || ""}`}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setControlsVisible(!isPlaying)}
    >
      <video
        ref={videoRef}
        className={s.video}
        src={src}
        poster={poster}
        preload="metadata"
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
      />

      {isBuffering && <div className={s.loadingOverlay}>Buffering...</div>}

      <div
        className={`${s.controls} ${
          controlsVisible || !isPlaying ? s.controlsVisible : ""
        }`}
      >
        <div className={s.progressWrapper}>
          <input
            className={s.progress}
            type="range"
            min={0}
            max={duration || 1}
            step={0.1}
            value={currentTime}
            style={
              {
                "--progress": `${progressPercent}%`,
              } as React.CSSProperties
            }
            onChange={(event) => handleSeek(Number(event.target.value))}
            aria-label="Seek"
          />
          <div className={s.progressTime}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        <div className={s.actions}>
          <div className={s.leftControls}>
            <IconButton
              className={s.iconButton}
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>

            <IconButton
              className={`${s.iconButton} ${s.skipControl}`}
              onClick={() => handleSkip(-10)}
              aria-label="Back 10 seconds"
            >
              <Replay10Icon />
            </IconButton>
            <IconButton
              className={`${s.iconButton} ${s.skipControl}`}
              onClick={() => handleSkip(10)}
              aria-label="Forward 10 seconds"
            >
              <Forward10Icon />
            </IconButton>

            <div className={s.volumeBlock}>
              <IconButton
                className={s.iconButton}
                onClick={toggleMute}
                aria-label={isMuted ? "Unmute" : "Mute"}
              >
                {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
              </IconButton>
              <input
                className={s.volume}
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={(event) =>
                  handleVolumeChange(Number(event.target.value))
                }
                aria-label="Volume"
              />
            </div>

            <div className={`${s.speedSelector} ${s.mobileOnly}`}>
              <button
                type="button"
                className={s.speedButton}
                onClick={() => setShowSpeedMenu((prev) => !prev)}
                aria-label="Playback speed"
              >
                <SettingsIcon />
                <span>{playbackRate}x</span>
              </button>
              {showSpeedMenu && (
                <div className={s.speedMenu}>
                  {SPEEDS.map((speed) => (
                    <button
                      key={speed}
                      type="button"
                      className={`${s.speedItem} ${
                        speed === playbackRate ? s.speedActive : ""
                      }`}
                      onClick={() => handleSpeedChange(speed)}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={s.rightControls}>
            <div className={s.speedSelector} ref={speedControlRef}>
              <button
                type="button"
                className={s.speedButton}
                onClick={() => setShowSpeedMenu((prev) => !prev)}
                aria-label="Playback speed"
              >
                <SettingsIcon />
                <span>{playbackRate}x</span>
              </button>
              {showSpeedMenu && (
                <div className={s.speedMenu}>
                  {SPEEDS.map((speed) => (
                    <button
                      key={speed}
                      type="button"
                      className={`${s.speedItem} ${
                        speed === playbackRate ? s.speedActive : ""
                      }`}
                      onClick={() => handleSpeedChange(speed)}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
              )}
            </div>

            <IconButton
              className={s.iconButton}
              onClick={togglePictureInPicture}
              aria-label="Picture in picture"
            >
              <PictureInPictureAltIcon />
            </IconButton>

            <IconButton
              className={s.iconButton}
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>

            {allowDownload && (
              <IconButton
                className={s.iconButton}
                onClick={handleDownload}
                aria-label="Download video"
                disabled={isDownloading}
              >
                <DownloadIcon />
              </IconButton>
            )}
          </div>

          <div className={s.mobileControls} ref={mobileMenuRef}>
            <IconButton
              className={s.iconButton}
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              aria-label="More actions"
            >
              <MoreVertIcon />
            </IconButton>
            {isMobileMenuOpen && (
              <div className={s.mobileMenu}>
                <button
                  type="button"
                  className={s.mobileMenuItem}
                  onClick={togglePictureInPicture}
                >
                  <PictureInPictureAltIcon />
                  <span>PiP</span>
                </button>

                <button
                  type="button"
                  className={s.mobileMenuItem}
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                  <span>{isFullscreen ? "Collapse" : "Fullscreen"}</span>
                </button>

                {allowDownload && (
                  <button
                    type="button"
                    className={s.mobileMenuItem}
                    onClick={handleDownload}
                    disabled={isDownloading}
                  >
                    <DownloadIcon />
                    <span>Download</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {title && <div className={s.titleBadge}>{title}</div>}
    </div>
  );
}
